import { FormikProps } from 'formik';
import { InputFormik } from '../../Common';
import { useEffect, useState, useRef } from 'react';

interface ITimeInputFormikProps<T> {
    context: FormikProps<T>;
    id: string;
    label?: string;
    placeholder?: string;
    onTimeChange: (time: string) => void;
    disabled?: boolean;
    className?: string;
    isRequired?: boolean;
}

export const TimeInputFormik = <T extends object>({
    id,
    context,
    label,
    placeholder,
    onTimeChange,
    disabled,
    className,
    isRequired = true,
}: ITimeInputFormikProps<T>) => {
    const [displayTimePicker, setDisplayTimePicker] = useState(false);
    const [selectedHour, setSelectedHour] = useState<number>(9);
    const [selectedMinute, setSelectedMinute] = useState<number>(0);
    const timePickerRef = useRef<HTMLDivElement>(null);

    const currentValue = context.values[id as keyof T] as string;

    useEffect(() => {
        if (currentValue) {
            const [hours, minutes] = currentValue.split(':').map(Number);
            if (!isNaN(hours) && !isNaN(minutes)) {
                setSelectedHour(hours);
                setSelectedMinute(minutes);
            }
        }
    }, [currentValue]);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (timePickerRef.current && !timePickerRef.current.contains(event.target as Node)) {
                setDisplayTimePicker(false);
            }
        };

        if (displayTimePicker) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [displayTimePicker]);

    const handleTimeSelection = (hour: number, minute: number) => {
        const formattedTime = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
        context.setFieldValue(id, formattedTime);
        onTimeChange(formattedTime);
        setDisplayTimePicker(false);
    };

    const hours = Array.from({ length: 24 }, (_, i) => i);
    const minutes = Array.from({ length: 60 }, (_, i) => i);

    const formattedTime = currentValue || '';

    return (
        <div className={`time-picker-input ${displayTimePicker ? 'open' : ''} ${className || ''}`} ref={timePickerRef}>
            <div className="time-picker-input__area">
                <InputFormik
                    context={context}
                    id={id}
                    iconSrc="/svg/time.svg"
                    label={label}
                    placeholder={placeholder}
                    iconOnClick={() => setDisplayTimePicker(prev => !prev)}
                    isRequired={isRequired}
                    isDisabled={disabled}
                    value={formattedTime}
                    onChange={() => {}}
                />
                {displayTimePicker && (
                    <div className="time-picker-input__dropdown">
                        <div className="time-picker-input__selectors">
                            <div className="time-picker-input__selector">
                                <div className="time-picker-input__selector-label">Hour</div>
                                <div className="time-picker-input__selector-list">
                                    {hours.map(hour => (
                                        <div
                                            key={hour}
                                            className={`time-picker-input__selector-item ${hour === selectedHour ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedHour(hour);
                                                handleTimeSelection(hour, selectedMinute);
                                            }}
                                        >
                                            {hour.toString().padStart(2, '0')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="time-picker-input__selector">
                                <div className="time-picker-input__selector-label">Minute</div>
                                <div className="time-picker-input__selector-list">
                                    {minutes.map(minute => (
                                        <div
                                            key={minute}
                                            className={`time-picker-input__selector-item ${minute === selectedMinute ? 'active' : ''}`}
                                            onClick={() => {
                                                setSelectedMinute(minute);
                                                handleTimeSelection(selectedHour, minute);
                                            }}
                                        >
                                            {minute.toString().padStart(2, '0')}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
